import { useVersionCheck } from '@src/hooks/useVersionCheck';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const logger = LogManager.getLogger('hook:UpdateAvailability');

interface UpdateAvailability {
  /** True when a newer deployment is available and should be surfaced to the user. */
  isUpdateAvailable: boolean;
  /** True while onUpdate is saving/reloading, so the UI can show pending feedback. */
  isUpdating: boolean;
  /** Save any unsaved work, then reload the page to pick up the new version. */
  onUpdate: () => void;
}

const UpdateAvailabilityContext = createContext<UpdateAvailability>({
  isUpdateAvailable: false,
  isUpdating: false,
  onUpdate: () => {},
});

/**
 * Read the current update-availability state. Consumed by the sidebar update
 * card (see SidebarUpdateCard) to render a passive "Update Available" prompt
 * instead of a blocking popup.
 */
export const useUpdateAvailability = () =>
  useContext(UpdateAvailabilityContext);

interface UpdateAvailabilityProviderProps {
  children: React.ReactNode;
  checkInterval?: number;
  enabled?: boolean;
}

/**
 * Runs the version-check poll once, above the router (mounted in EditorEntry),
 * and exposes the result via context so the sidebar can show an unobtrusive
 * update card. Keeping the poll here (rather than inside the sidebar or a
 * per-route layout) means it runs as a single instance regardless of route,
 * even when the sidebar is collapsed or hidden.
 */
export const UpdateAvailabilityProvider: React.FC<UpdateAvailabilityProviderProps> =
  observer(({ children, checkInterval = 5 * 60 * 1000, enabled = true }) => {
    const videoCreatorStore = useVideoCreatorStore();
    const [isUpdating, setIsUpdating] = useState(false);

    const hookResult = useVersionCheck({ checkInterval, enabled });
    const {
      isUpdateAvailable: detectedUpdate = false,
      refreshPage = () => {},
      forceReloadEnabled = false,
    } = hookResult || {};

    // Dev-only override: version checks are disabled locally (no build SHA), so
    // set REACT_APP_FORCE_UPDATE_AVAILABLE=true to force the card on for testing.
    const isUpdateAvailable =
      detectedUpdate || process.env.REACT_APP_FORCE_UPDATE_AVAILABLE === 'true';

    const onUpdate = useCallback(async () => {
      // Guard against re-entrant clicks while the save/reload is in flight.
      setIsUpdating(true);
      // Save the current work before refreshing.
      if (videoCreatorStore?.saveStoryAndVideo) {
        try {
          await videoCreatorStore.saveStoryAndVideo();
          await videoCreatorStore.waitForBackgroundSave();
        } catch (error) {
          logger.error('Failed to save before refresh', {
            error: extractErrorInfo(error),
          });
        }
      }
      refreshPage();
    }, [refreshPage, videoCreatorStore]);

    // Force reload mode: refresh automatically as soon as an update is detected.
    useEffect(() => {
      if (isUpdateAvailable && forceReloadEnabled) {
        logger.info('Force reload enabled - automatically refreshing page');
        onUpdate();
      }
    }, [isUpdateAvailable, forceReloadEnabled, onUpdate]);

    const value = useMemo(
      () => ({
        isUpdateAvailable: isUpdateAvailable && !forceReloadEnabled,
        isUpdating,
        onUpdate,
      }),
      [isUpdateAvailable, forceReloadEnabled, isUpdating, onUpdate],
    );

    return (
      <UpdateAvailabilityContext.Provider value={value}>
        {children}
      </UpdateAvailabilityContext.Provider>
    );
  });
